<script setup lang="ts">
import { computed } from 'vue';

import { SKU_COLLAPSE_STYLE, SKU_STYLE_TYPE } from '@/store/constants';
import { useGlobalStyle } from '@/store/templateData';

import ModuleProductListFlattenDisplay from '../ModuleProductListFlattenDisplay/ModuleProductListFlattenDisplay.vue';
import ModuleProductListSkuDisplay from '../ModuleProductListSkuDisplay/ModuleProductListSkuDisplay.vue';

interface ModuleProductListProps {
  module: {
    components: {
      title: {
        text: string;
      };
      subtitle: {
        text: string;
      };
      name: {
        textSize: string;
        lineHeight: string;
        textColor: string;
      };
      skuStyle: {
        type: 'expand' | 'collapse';
        collapseStyle: 'square' | 'circle' | 'hidden';
      };
      originalPrice: {
        enabled: boolean;
        textChoice: number;
      };
      price: {
        enabled: boolean;
        textSize: string;
        lineHeight: string;
        textColor: string;
      };
      shopcart: {
        enabled: boolean;
      };
      button: {
        text: string;
        textSize: string;
        textColor: string;
        lineHeight: string;
        fillStyle: 'outline' | 'solid';
      };
    };
  };
}

defineOptions({
  name: 'ModuleProductListDisplay'
});

const props = withDefaults(defineProps<ModuleProductListProps>(), {
  module: () => ({
    components: {
      title: {
        text: '',
      },
      subtitle: {
        text: '',
      },
      name: {
        textSize: '',
        lineHeight: '',
        textColor: '',
      },
      skuStyle: {
        type: SKU_STYLE_TYPE.EXPAND as 'expand',
        collapseStyle: SKU_COLLAPSE_STYLE.SQUARE as 'square',
      },
      originalPrice: {
        enabled: true,
        textChoice: 0,
      },
      price: {
        enabled: true,
        textSize: '',
        lineHeight: '',
        textColor: '',
      },
      shopcart: {
        enabled: true,
      },
      button: {
        text: '',
        textSize: '',
        textColor: '',
        lineHeight: '',
        fillStyle: 'outline' as 'outline',
      },
    },
  }),
});

const components = computed(() => props.module.components);
const shouldFlattenProductSkuList = computed(() => {
  return components.value.skuStyle.type === SKU_STYLE_TYPE.EXPAND;
});

const { titleStyle, subtitleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-product-list-display">
  <div
    class="module-product-list-display__title"
    v-if="components.title.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>
  <div
    class="module-product-list-display__subtitle"
    v-if="components.subtitle.text"
    :style="subtitleStyle"
  >
    {{ components.subtitle.text }}
  </div>

  <template v-if="shouldFlattenProductSkuList">
    <ModuleProductListFlattenDisplay :module="module"/>
  </template>
  <template v-else>
    <ModuleProductListSkuDisplay :module="module"/>
  </template>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-product-list-display {
  @apply px-5 py-4;

  &__title {
    @apply text-4xl text-center whitespace-pre-line;
  }

  &__subtitle {
    @apply text-xl text-left whitespace-pre-line;
  }

  &__title,
  &__subtitle {
    @apply mb-4 font-medium break-all;

    word-wrap: break-word;
  }
}

.tablet .module-product-list-display {
  @apply px-10;
}

.laptop .module-product-list-display {
  @apply py-8;

  padding-left: 10%;
  padding-right: 10%;
}

.desktop .module-product-list-display {
  @apply py-8;

  width: $desktop-fixed-width;
}
</style>
